import React, { useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import { IconButton } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { toPng } from 'html-to-image';
import 'reactflow/dist/style.css';

// Import custom edge component and types if they're defined elsewhere
import { edgeTypes } from '../utils/flowUtils'; // You'll need to create this

const FlowContainer = ({
  type,
  nodes,
  setNodes,
  edges,
  setEdges,
  nodeTypes,
}) => {
  const reactFlowWrapper = useRef(null);

  const onNodesChange = useCallback(
    (changes) =>
      setNodes((nds) => {
        if (nds.length === 0) return nds;
        const lastNode = nds[nds.length - 1];
        const updatedLastNode = applyNodeChanges(changes, lastNode);
        return [...nds.slice(0, nds.length - 1), updatedLastNode];
      }),
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) =>
      setEdges((eds) => {
        if (!Array.isArray(eds) || eds.length === 0) return eds;
        const lastEdge = eds[eds.length - 1];
        const updatedLastEdge = applyEdgeChanges(changes, lastEdge);
        return [...eds.slice(0, eds.length - 1), updatedLastEdge];
      }),
    [setEdges]
  );

  const onConnect = useCallback(
    (connection) =>
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#64748b', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: '#64748b',
            },
            labelStyle: { fill: '#64748b', fontWeight: 500 },
            labelBgStyle: { fill: 'white' },
            labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
          },
          eds
        )
      ),
    [setEdges]
  );

  const saveAsImage = useCallback(() => {
    if (reactFlowWrapper.current) {
      const scale = 3;
      const wrapperElement = reactFlowWrapper.current;
      const width = wrapperElement.offsetWidth;
      const height = wrapperElement.offsetHeight;

      toPng(wrapperElement, {
        quality: 1,
        backgroundColor: '#fafafa',
        width: width * scale,
        height: height * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${width}px`,
          height: `${height}px`,
        },
      })
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `${type}-diagram.png`;
          link.click();
        })
        .catch((error) => {
          console.error('Error saving the image:', error);
        });
    }
  }, [type]);

  return (
    <div style={{ width: '100%', height: '100%' }} className="flow-container">
      <IconButton
        onClick={saveAsImage}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          backgroundColor: 'black',
          color: 'white',
          zIndex: 1000,
          padding: '8px',
          '&:hover': {
            backgroundColor: '#333333',
          },
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
        size="small"
        title="Save as Image"
      >
        <DownloadIcon />
      </IconButton>
      <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          defaultViewport={{ zoom: 1 }}
          minZoom={0.01}
          maxZoom={5}
          snapToGrid={false}
          connectionMode="loose"
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed },
          }}
          style={{ background: '#fafafa' }}
        >
          <Background color="#e5e5e5" gap={16} size={1} />
          <Controls
            className="m-4 bg-white shadow-lg rounded-lg border border-gray-200"
            style={{
              button: {
                backgroundColor: '#1a1a1a',
                color: 'white',
                '&:hover': { backgroundColor: '#333333' },
              },
            }}
            showInteractive={false}
          />
          <MiniMap
            className="m-4 rounded-lg shadow-lg"
            nodeColor={() => '#1a1a1a'}
            maskColor="rgba(0, 0, 0, 0.05)"
          />
        </ReactFlow>
      </div>
    </div>
  );
};

export default FlowContainer; 